import axios from 'axios';

const baseUrl = process.env.VUE_APP_URL || 'https://web-api.govava.com/';
const SESSION_STORAGE_KEY = 'govava_session_id';

const getOrCreateSessionId = () => {
  try {
    let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  } catch (err) {
    return null;
  }
};

const getCustomerContext = () => {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const user = parsed?.user;
    if (!user?.id) return null;
    const token = parsed?.token || user?.api_token || user?.token;
    return { customerId: user.id, token };
  } catch (err) {
    return null;
  }
};

export const getEventContext = () => {
  const customer = getCustomerContext();
  if (customer) {
    return { customerId: customer.customerId, token: customer.token };
  }

  const sessionId = getOrCreateSessionId();
  if (!sessionId) return null;

  return { sessionId };
};

export const getProductId = (product) => {
  if (!product) return null;
  return product.productId || product.product_id || product.sku || product.id || null;
};

export const buildProductSnapshot = (product) => {
  if (!product || typeof product !== 'object') return null;

  const productId = getProductId(product);
  const snapshot = {};

  if (productId) {
    snapshot.productId = String(productId);
  }

  ['productname', 'imageurl', 'linkurl', 'sku'].forEach((key) => {
    if (product[key] != null && product[key] !== '') {
      snapshot[key] = product[key];
    }
  });

  if (product.saleprice != null) {
    snapshot.saleprice = product.saleprice;
  }

  if (product.price != null) {
    snapshot.price = product.price;
  }

  return Object.keys(snapshot).length > 1 ? snapshot : null;
};

export const trackUserEvent = async ({
  eventType,
  productId = null,
  keyword = null,
  metadata = null,
} = {}) => {
  const ctx = getEventContext();
  if (!ctx || !eventType) return;

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };
    if (ctx.token) {
      headers.Authorization = `Bearer ${ctx.token}`;
    }

    const payload = {
      event_type: eventType,
      product_id: productId,
      keyword,
      metadata,
    };

    if (ctx.customerId) {
      payload.customer_id = ctx.customerId;
    } else if (ctx.sessionId) {
      payload.session_id = ctx.sessionId;
    }

    await axios.post(`${baseUrl}customer/user/events`, payload, { headers });
  } catch (err) {
    console.warn('Failed to track user event:', err);
  }
};

export const trackProductEvent = async ({
  eventType,
  productId = null,
  metadata = null,
} = {}) => {
  return trackUserEvent({ eventType, productId, metadata });
};

export const trackProductClick = (product, source = 'unknown') => {
  const productId = getProductId(product);
  if (!productId) return;

  const snapshot = buildProductSnapshot(product);
  const metadata = { source };
  if (snapshot) {
    metadata.product = snapshot;
  }

  return trackProductEvent({
    eventType: 'product_click',
    productId: String(productId),
    metadata,
  });
};

export const trackProductFav = (product, source = 'unknown') => {
  const productId = getProductId(product);
  if (!productId) return;

  const snapshot = buildProductSnapshot(product);
  const metadata = { source };
  if (snapshot) {
    metadata.product = snapshot;
  }

  return trackProductEvent({
    eventType: 'add_to_fav',
    productId: String(productId),
    metadata,
  });
};

export const trackProductWishlist = (product, source = 'unknown') => {
  const productId = getProductId(product);
  if (!productId) return;

  const snapshot = buildProductSnapshot(product);
  const metadata = { source };
  if (snapshot) {
    metadata.product = snapshot;
  }

  return trackProductEvent({
    eventType: 'wishlist_save',
    productId: String(productId),
    metadata,
  });
};

export const trackProductCart = (product, source = 'unknown') => {
  const productId = getProductId(product);
  if (!productId) return;

  const snapshot = buildProductSnapshot(product);
  const metadata = { source };
  if (snapshot) {
    metadata.product = snapshot;
  }

  return trackProductEvent({
    eventType: 'add_to_cart',
    productId: String(productId),
    metadata,
  });
};

export const feedAPI = {
  async getForYou(limit = 24) {
    const ctx = getCustomerContext();
    if (!ctx) {
      return { status: false, personalized: false, response: { items: [] } };
    }

    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };
    if (ctx.token) {
      headers.Authorization = `Bearer ${ctx.token}`;
    }

    const { data } = await axios.post(
      `${baseUrl}customer/feed/for-you`,
      {
        customer_id: ctx.customerId,
        limit,
      },
      { headers }
    );

    return data;
  },

  async getTrending(limit = 24) {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };

    const ctx = getCustomerContext();
    if (ctx?.token) {
      headers.Authorization = `Bearer ${ctx.token}`;
    }

    const { data } = await axios.post(
      `${baseUrl}customer/feed/trending`,
      { limit },
      { headers }
    );

    return data;
  },
};
